/**
 * The end of the stage row when it is not the run strip
 * (specs/admin-instance-lifecycle.md, "Making it flow"):
 * - LiveChip: the workshop is running — its step, and one click back to
 *   Työpaja. Right after leaving Toteutus it says, for a moment, that the
 *   workshop and the screens keep going (the quiet confirmation).
 * - NextStep: otherwise, for admins, the next step of the stage the
 *   instance is in (utils/stageStatus.js), or the move into the next stage.
 */
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { ArrowRight, Radio } from 'lucide-react';
import useInstanceRecord from '../../hooks/useInstanceRecord';
import useInstanceOverview from '../../hooks/useInstanceOverview';
import { buildStageStatus, itemHref, STAGE_ORDER } from '../../utils/stageStatus';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { media } from '../../styles/breakpoints';

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  flex-shrink: 0;
  max-width: 420px;
  padding: 5px 10px;
  border-radius: 8px;
  border: 1px solid ${p => (p.$live ? 'rgba(74, 222, 128, 0.45)' : 'rgba(37, 99, 235, 0.5)')};
  background: ${p => (p.$live ? 'rgba(74, 222, 128, 0.1)' : 'rgba(37, 99, 235, 0.12)')};
  color: ${p => (p.$live ? '#bbf7d0' : '#bfdbfe')};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover { filter: brightness(1.15); }
  .dim { font-weight: 500; opacity: 0.75; }

  ${media.sm} { .wide { display: none; } }
`;

export function LiveChip({ chain, justLeft, onNavigate, buildPath }) {
  const { t } = useTranslation();
  const [emphasis, setEmphasis] = useState(justLeft);
  useEffect(() => {
    if (!justLeft) return undefined;
    setEmphasis(true);
    const timer = setTimeout(() => setEmphasis(false), 6000);
    return () => clearTimeout(timer);
  }, [justLeft]);

  const steps = chain?.steps || [];
  const index = steps.findIndex((s) => s.isLive);
  const step = steps[index];
  return (
    <Chip type="button" $live onClick={() => onNavigate(buildPath('/admin/workshop'))} title={t('stageRow.backToWorkshop')}>
      <Radio size={14} />
      {emphasis ? t('stageRow.stillRunning') : t('stageRow.live')}
      {step && <span className="dim wide">· {t('stageRow.step', { current: index + 1, total: steps.length })}</span>}
      <ArrowRight size={14} />
    </Chip>
  );
}

export function NextStep({ instanceId, chain, onNavigate, buildPath }) {
  const { t } = useTranslation();
  const { instance } = useInstanceRecord(instanceId);
  const { overview } = useInstanceOverview(instanceId);
  if (!instance || !overview || !chain) return null;

  const status = buildStageStatus({ instance, chain, overview });
  const stage = status.stages[status.current];
  const nextStage = STAGE_ORDER[STAGE_ORDER.indexOf(status.current) + 1];
  if (!stage.next && !nextStage) return null;

  const target = stage.next ? itemHref(stage.next, instanceId) : buildPath('/admin');
  const text = stage.next
    ? t(`stageStatus.${stage.next.key}`, stage.next.values || {})
    : t(`adminHome.moveTo_${nextStage}`);
  return (
    <Chip type="button" onClick={() => onNavigate(target)} title={text}>
      <span className="dim">{t('adminHome.nextStep')}:</span>
      <span className="wide">{text}</span>
      <ArrowRight size={14} />
    </Chip>
  );
}
